import { useContactList } from "@/api/services/admin/contact";
import { useContactTypeList } from "@/api/services/admin/contact-type";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { Tab } from "@/components/elements/Tab";
import { TopPage } from "@/components/elements/TopPage";
import { CONTACT_TYPE } from "@/constants/enum";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { ContactListPagination } from "@/features/contact/components/ContactListPagination";
import { ContactListTop } from "@/features/contact/components/ContactListTop";
import { ContactTable } from "@/features/contact/components/ContactTable";
import { ContactTypeListTop } from "@/features/contact/components/ContactTypeListTop";
import { ContactTypeTable } from "@/features/contact/components/ContactTypeTable";
import { CONTACT_LIST_TAB, CONTACT_LIST_TAB_LABEL } from "@/features/contact/constants/contactListTab";
import { useContactFilterCache } from "@/features/contact/provider/ContactListFilterCacheProvider";
import { background, divider } from "@/theme/colors";

import { Box, Stack } from "@mui/material";
import { FC, useMemo } from "react";

type TContactListPageProps = {
  isCompany: boolean;
};

export const ContactListPage: FC<TContactListPageProps> = ({ isCompany }) => {
  const { contactFilterCache, setContactFilterCache } = useContactFilterCache();

  const searchTab = contactFilterCache?.tab || CONTACT_LIST_TAB.UN_RESOLVED;

  const { data: contactListResult, isLoading: isLoadingContact } = useContactList(
    {
      isResolved: [searchTab === CONTACT_LIST_TAB.RESOLVED ? true : false],
      type: isCompany ? CONTACT_TYPE.COMPANY : CONTACT_TYPE.RSO,
      page: contactFilterCache?.page || START_PAGE,
      perPage: DEFAULT_PER_PAGE,
    },
    [CONTACT_LIST_TAB.RESOLVED, CONTACT_LIST_TAB.UN_RESOLVED].includes(searchTab),
  );

  const { data: contactTypeListResult, isLoading: isLoadingContactType } = useContactTypeList(
    {
      type: isCompany ? CONTACT_TYPE.COMPANY : CONTACT_TYPE.RSO,
      page: contactFilterCache?.page || START_PAGE,
      perPage: DEFAULT_PER_PAGE,
    },
    searchTab === CONTACT_LIST_TAB.CONTACT_TYPE,
  );

  const isLoading = useMemo(() => isLoadingContact || isLoadingContactType, [isLoadingContact, isLoadingContactType]);

  return (
    <Stack gap={2.5}>
      <TopPage title="お問い合わせ管理" enablePageBack={false} />
      <Stack
        sx={{
          border: `1px solid ${divider.middle}`,
          borderTop: "none",
          backgroundColor: background.white,
          borderRadius: "8px",
        }}
      >
        <Tab
          value={searchTab}
          onChange={(_, tab) => {
            setContactFilterCache({ tab: tab.toString(), page: START_PAGE });
          }}
          options={[
            {
              label: `${CONTACT_LIST_TAB_LABEL[CONTACT_LIST_TAB.UN_RESOLVED]}`,
              value: CONTACT_LIST_TAB.UN_RESOLVED,
            },
            {
              label: `${CONTACT_LIST_TAB_LABEL[CONTACT_LIST_TAB.RESOLVED]} `,
              value: CONTACT_LIST_TAB.RESOLVED,
            },
            {
              label: `${CONTACT_LIST_TAB_LABEL[CONTACT_LIST_TAB.CONTACT_TYPE]}`,
              value: CONTACT_LIST_TAB.CONTACT_TYPE,
            },
          ]}
        />
        {isLoading ? (
          <Box p={3}>
            <LoadingArea />
          </Box>
        ) : (
          <>
            {[CONTACT_LIST_TAB.RESOLVED, CONTACT_LIST_TAB.UN_RESOLVED].includes(searchTab) && (
              <Stack gap={3} padding="24px">
                <ContactListTop pagination={contactListResult?.pagination} />
                {contactListResult && (
                  <ContactTable
                    isCompany={isCompany}
                    isLoading={isLoading}
                    isResolvedTab={searchTab === CONTACT_LIST_TAB.RESOLVED}
                    data={contactListResult ?? []}
                  />
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <ContactListPagination totalPage={contactListResult?.pagination.totalPage} />
                </Stack>
              </Stack>
            )}
            {searchTab === CONTACT_LIST_TAB.CONTACT_TYPE && (
              <Stack gap={3} padding="24px">
                <ContactTypeListTop isCompany={isCompany} pagination={contactTypeListResult?.pagination} />
                {contactTypeListResult && <ContactTypeTable isCompany={isCompany} isLoading={isLoading} data={contactTypeListResult ?? []} />}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <ContactListPagination totalPage={contactTypeListResult?.pagination.totalPage} />
                </Stack>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};
